import { useContext, useState } from 'react';
import { QueryClient } from 'react-query';
import dynamic from 'next/dynamic';
import type { NextPage } from 'next';
import Head from 'next/head';
import cn from 'classnames';
import withStore from 'context/Store/withStore';
import { LanguageContext } from 'context/Language';
import { StoreContext } from 'context/Store';
import { articles, faq } from 'api';
import { defaultOptions, getQueries, getSeoTitle, getSeoDescription, cacheTime } from 'utils';
import { Properties } from 'types';
import { Article } from 'resources';

const Container = dynamic(() => import('components/Grid/Container'));
const Row = dynamic(() => import('components/Grid/Row'));
const Col = dynamic(() => import('components/Grid/Col'));
const WarningSlider = dynamic(() => import('components/WarningsSlider'));
const Section = dynamic(() => import('components/Layout/Section'));
const SiderMenu = dynamic(() => import('components/Layout/SiderMenu'));
const Icon = dynamic(() => import('components/Icon'));
const Collapse = dynamic(() => import('components/Collapse'));
const Responsive = dynamic(() => import('components/Responsive'));

const FrequentlyAskedQuestionPage: NextPage<Properties> = ({ data, dataFAQ }) => {
  const { lang } = useContext(LanguageContext);
  const { url } = useContext(StoreContext);

  return (
    <>
      <Head>
        <title>{getSeoTitle(data?.i18n && data.i18n[lang]?.title)}</title>
        {data?.i18n && data?.i18n[lang]?.preview && (
          <meta name="description" content={getSeoDescription(data?.i18n[lang]?.preview)} />
        )}

        <meta property="og:url" content={url} />
        <meta name="og:title" content={getSeoTitle(data?.i18n && data?.i18n[lang]?.title)} />
        {data?.i18n && (data?.i18n[lang]?.preview || data?.i18n[lang]?.content) && (
          <meta
            property="og:description"
            content={getSeoDescription(data?.i18n[lang]?.preview || data?.i18n[lang]?.content)}
          />
        )}
        {data?.background && <meta property="og:image" content={data?.background} />}
      </Head>

      <>
        <Container className={cn('pt-20')}>
          <Row g={4}>
            <Col size={12} lg={3}>
              <SiderMenu data={data} />
            </Col>
            <Col size={12} lg={9}>
              <Row g={3}>
                <Col size={12}>
                  <Section>
                    <h1 className="section__title">{data?.i18n && data?.i18n[lang]?.title}</h1>
                  </Section>
                </Col>
                <Col size={12}>
                  <Section>
                    <div
                      className="section-text__content"
                      dangerouslySetInnerHTML={{ __html: data?.i18n && data?.i18n[lang]?.content }}
                    />
                  </Section>
                </Col>

                {dataFAQ?.results &&
                  dataFAQ.results.map((item) => (
                    <Col size={12} key={item.id}>
                      <Collapse
                        icon={<Icon component={Article} />}
                        header={item?.i18n[lang]?.question}
                        collapsed
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: item?.i18n && item?.i18n[lang]?.answer,
                          }}
                        />
                      </Collapse>
                    </Col>
                  ))}
              </Row>
            </Col>
          </Row>
        </Container>
        <Responsive>
          <div className="separated-border__sm mt-70 mb-30" />{' '}
        </Responsive>
        <Section className={cn('pt-40', 'pb-40')}>
          <WarningSlider />
        </Section>
      </>
    </>
  );
};

FrequentlyAskedQuestionPage.getInitialProps = async ({ query }): Promise<Properties> => {
  const queryClient = new QueryClient(defaultOptions);

  await queryClient.prefetchQuery(
    ['data', { id: query?.slug }],
    (props) => articles.get(props, { 'Accept-Language': query?.lang }),
    { cacheTime },
  );

  await queryClient.prefetchQuery(
    ['dataFAQ', { page_size: 100, ordering: 'timestamp' }],
    (props) => faq.getList(props, { 'Accept-Language': query?.lang }),
    { cacheTime },
  );

  return getQueries(queryClient);
};

export default withStore()(FrequentlyAskedQuestionPage);
